import { FinancingConfig, InstallmentConfig, InstallmentExample } from '../types/installment-plan-calculator';
import isEmpty from 'lodash/isEmpty';

const DELAYED_PAYMENT_FEE_PERCENT = 3.5;

export type InstallmentPricing = {
    duration: number;
    surcharge: number;
    monthlyRateInEuro: number;
};

export const defaultDurations: InstallmentExample[] = [
    { duration: 12, min: 1, max: 24999 },
    { duration: 24, min: 25000, max: 39999 },
    { duration: 36, min: 40000, max: 49999 },
    { duration: 48, min: 50000, max: 1000000000000 },
];

export function getValidatedDefaultDuration(retailPrice: number | undefined, installmentConfigs: InstallmentConfig[]) {
    if (!retailPrice || retailPrice < 0) {
        return 0;
    }
    const defaultDuration = getDefaultDuration(retailPrice);
    return validateAndCorrectDefaultDuration(defaultDuration, installmentConfigs) || 0;
}

export function getDefaultDuration(retailPrice: number | undefined): number {
    if (!retailPrice || retailPrice < 0) {
        return defaultDurations[0].duration;
    }

    const defaultInstallmentPlan = defaultDurations.find(i => i.min <= retailPrice && i.max >= retailPrice);
    if (!defaultInstallmentPlan) {
        return defaultDurations[0].duration;
    }

    return defaultInstallmentPlan.duration;
}

export function validateAndCorrectDefaultDuration(defaultDuration: number, installmentConfigs: InstallmentConfig[]): number | undefined {
    const hasSameDuration = installmentConfigs.find(c => c.duration === defaultDuration);
    if (hasSameDuration) {
        return defaultDuration;
    }

    const higherDuration = installmentConfigs.find(c => c.duration > defaultDuration)?.duration;
    if (higherDuration) {
        return higherDuration;
    }

    return [...installmentConfigs].reverse().find(c => c.duration < defaultDuration)?.duration;
}

export function getDefaultInstallmentConfig(defaultDuration: number | undefined, installmentPlans: InstallmentConfig[]) {
    return installmentPlans.find(plan => plan.duration === defaultDuration);
}

export function getDefaultInstallmentPricing(
    retailPriceInCents: number | undefined,
    financingConfig?: FinancingConfig | Record<string, never>
): InstallmentPricing | undefined {
    const installmentConfigs = financingConfig?.installmentConfig || [];
    const duration = getValidatedDefaultDuration(retailPriceInCents, installmentConfigs);

    if (!hasRequiredDataToRender(retailPriceInCents, financingConfig, duration)) {
        return undefined;
    }

    const installmentConfig = getDefaultInstallmentConfig(duration, installmentConfigs);
    if (!installmentConfig) {
        return undefined;
    }

    return {
        duration: installmentConfig.duration,
        surcharge: installmentConfig.surcharge,
        monthlyRateInEuro: calcInstallmentRate(installmentConfig, retailPriceInCents || 0),
    };
}

export function hasRequiredDataToRender(
    retailPrice?: number,
    financingConfig?: FinancingConfig | Record<string, never>,
    duration?: number
): boolean {
    if (!retailPrice) {
        return false;
    }

    if (isEmpty(financingConfig) || !financingConfig?.installmentConfig?.length) {
        return false;
    }

    return !!duration;
}

/*                                                    */
/*                                 */
/*                                                    */

/**
 *
 *
 *
 *
 */
export function calcInstallmentRate(installmentConfig: InstallmentConfig, retailPrice: number): number {
    const installmentFactor = (1 + installmentConfig.surcharge / 100) ** installmentConfig.duration;
    const totalInstallmentAmount = retailPrice * installmentFactor;
    const installmentRate = totalInstallmentAmount / installmentConfig.duration;
    return round4Digits(installmentRate / 100);
}

export function getDelayedPaymentPriceInEuro(retailPriceInCents: number): number {
    return round2Digits((retailPriceInCents / 100) * (DELAYED_PAYMENT_FEE_PERCENT / 100));
}

export function formatEuroAmountWithNarrowNoBreakSpace(priceInEuro: number): string {
    return `${priceInEuro.toFixed(2).replace('.', ',')}\u202f€`;
}

function round4Digits(price: number): number {
    return parseFloat(price.toFixed(4));
}

function round2Digits(price: number): number {
    return parseFloat(price.toFixed(2));
}
